import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  EventEmitter,
  Inject,
  inject,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { urlValidator } from '@vendasta/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import {
  KnowledgeSourceConfig,
  KnowledgeSourceConfigWebsiteScrapeConfig,
  KnowledgeSourceConfigWebsiteScrapeConfigScrapedPage,
  KnowledgeSourceKey,
  WebsiteScrapeConfigCrawlMode,
  WebsiteScrapeConfigProgress,
} from '@vendasta/embeddings';
import { AiKnowledgeService } from '../../services/ai-knowledge.service';
import { MatIconModule } from '@angular/material/icon';
import { AiKnowledgeI18nModule } from '../../assets/i18n/ai-knowledge-i18n.module';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { MatOptionModule } from '@angular/material/core';
import { GalaxySnackbarModule, SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { MatInputModule } from '@angular/material/input';
import { defer, from, map, retry, take } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Environment, EnvironmentService } from '@galaxy/core';
import { GalaxyLoadingSpinnerModule } from '@vendasta/galaxy/loading-spinner';
import { MatStepper, MatStepperModule } from '@angular/material/stepper';
import { ApplicationSelectorComponent } from '../../application-selector/application-selector.component';
import { ScrapedPagesComponent, STEP_OPTION } from './scraped-pages/scraped-pages.component';
import { STEPPER_GLOBAL_OPTIONS, StepperSelectionEvent } from '@angular/cdk/stepper';
import { GalaxyEmptyStateModule } from '@vendasta/galaxy/empty-state';
import { MatButtonModule } from '@angular/material/button';
import { KnowledgeSourceWithSuggestedFlag, isTrainingFinished } from '../../ai-knowledge-util';
import { OpenConfirmationModalService } from '@vendasta/galaxy/confirmation-modal';
import { WebChatWidgetService } from '../../services/web-chat-widget.service';
import { Router } from '@angular/router';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { LastScrapedComponent } from './last-scraped/last-scraped.component';
import { KnowledgeCreatedEvent, StepChangeEvent } from '../common/interfaces';
import { AiLoadingSpinnerComponent } from '../../shared/ai-loading-spinner/ai-loading-spinner.component';
import { MatChipInputEvent, MatChipsModule } from '@angular/material/chips';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { MatCheckboxModule } from '@angular/material/checkbox';

interface Data {
  knowledgeSourceKey?: KnowledgeSourceKey;
  config?: KnowledgeSourceConfig;
  isSuggested?: boolean;
  stepOption?: string;
  excludeAddToAIStep?: boolean;
  currentAppId?: string;
}

@Component({
  selector: 'ai-knowledge-website-scrape',
  imports: [
    CommonModule,
    TranslateModule,
    AiKnowledgeI18nModule,
    MatIconModule,
    MatSelectModule,
    MatOptionModule,
    ReactiveFormsModule,
    FormsModule,
    GalaxyFormFieldModule,
    GalaxySnackbarModule,
    GalaxyLoadingSpinnerModule,
    MatInputModule,
    MatStepperModule,
    ScrapedPagesComponent,
    ApplicationSelectorComponent,
    GalaxyEmptyStateModule,
    MatButtonModule,
    LastScrapedComponent,
    MatDialogModule,
    AiLoadingSpinnerComponent,
    MatChipsModule,
    MatCheckboxModule,
  ],
  providers: [
    AiKnowledgeService,
    WebChatWidgetService,
    {
      provide: STEPPER_GLOBAL_OPTIONS,
      useValue: { displayDefaultIndicatorType: false },
    },
  ],
  templateUrl: './website-scrape.component.html',
  styleUrls: ['./website-scrape.component.scss'],
})
export class WebsiteScrapeComponent implements AfterViewInit, OnInit, KnowledgeCreatedEvent, StepChangeEvent {
  @ViewChild('stepper') stepper!: MatStepper;
  @ViewChild('scrapedPagesComponent') scrapedPagesComponent!: ScrapedPagesComponent;
  @ViewChild('applicationSelector') applicationSelector!: ApplicationSelectorComponent;

  private readonly snackbarService = inject(SnackbarService);
  private readonly environmentService = inject(EnvironmentService);
  private readonly knowledgeService = inject(AiKnowledgeService);
  private readonly translateService = inject(TranslateService);
  private readonly confirmationModal = inject(OpenConfirmationModalService);
  private readonly webChatWidgetService = inject(WebChatWidgetService);
  private readonly router = inject(Router);
  private readonly cdr = inject(ChangeDetectorRef);
  readonly isLoading = signal(true);
  readonly otherAIDisclaimer = signal('');

  websiteScrapeConfig: KnowledgeSourceConfigWebsiteScrapeConfig | undefined;
  crawlModes = WebsiteScrapeConfigCrawlMode;
  scrapeProgressStates = WebsiteScrapeConfigProgress;
  enableEditingWithError = false;
  form: FormGroup;

  steps = ['', STEP_OPTION];
  selectedStep = 0;
  // a string corresponding to the current step should be emitted whenever a step changes
  // this will be used to update the address to the current step, so that it can be linked to directly
  stepChange = new EventEmitter<string>();
  stepOption: string | undefined;

  scrapeProgressPagesFound = 0;
  delayProgressScreenClosure = false;
  excludeAddToAIStep = false;
  knowledgeSource: KnowledgeSourceWithSuggestedFlag | null = null;
  disableSubmit = false;
  shouldShowDone = signal(false);
  knowledgeCreated = new EventEmitter<string>();
  excludedPaths: string[] = [];
  excludedPathError = signal('');
  separatorKeysCodes: number[] = [ENTER, COMMA];

  constructor(
    readonly destroyRef: DestroyRef,
    public dialogRef: MatDialogRef<WebsiteScrapeComponent>,
    @Inject(MAT_DIALOG_DATA) public data: Data,
  ) {
    this.form = new FormGroup({
      name: new FormControl(''),
      url: new FormControl('', [urlValidator, Validators.required]),
      crawlMode: new FormControl(WebsiteScrapeConfigCrawlMode.WEBSITE_SCRAPE_CONFIG_CRAWL_MODE_SINGLE),
      automaticRefresh: new FormControl(false),
    });
  }

  async ngOnInit(): Promise<void> {
    this.dialogRef.disableClose = true;

    if (this.data) {
      if (this.data?.knowledgeSourceKey) {
        await this.knowledgeService
          .getKnowledgeSource(this.data.knowledgeSourceKey.knowledgeSourceId, this.data.knowledgeSourceKey.namespace)
          .then(
            (knowledgeSource) => {
              this.knowledgeSource = knowledgeSource as KnowledgeSourceWithSuggestedFlag;
              this.excludedPaths = knowledgeSource.config?.websiteScrapeConfig?.options?.excludedUrlPaths || [];
            },
            (_) => {
              this.knowledgeSource = null;
            },
          );

        this.otherAIDisclaimer.set(
          await this.knowledgeService.getOtherAIDisclaimerText(
            this.data.knowledgeSourceKey.knowledgeSourceId,
            this.data.currentAppId,
          ),
        );

        if (!this.knowledgeSource) {
          if (
            this.data.knowledgeSourceKey.knowledgeSourceId ===
            this.knowledgeService.buildBusinessWebsiteKnowledgeSourceId()
          ) {
            this.knowledgeSource = this.knowledgeService.buildBusinessWebsiteKnowledgeSource(
              this.data.config?.websiteScrapeConfig?.url,
              this.data.isSuggested,
            );
            this.cdr.detectChanges();
          }
        }

        this.configureStepper();
      }

      this.excludeAddToAIStep = this.data.excludeAddToAIStep ?? false;
    }

    this.isLoading.set(false);

    if (!this.excludeAddToAIStep) {
      this.steps.push('appSelection');
    }

    this.checkProgress();
    this.form = new FormGroup({
      name: new FormControl(this.knowledgeSource?.name || ''),
      url: new FormControl(this.websiteScrapeConfig?.url || '', [urlValidator, Validators.required]),
      crawlMode: new FormControl(
        this.websiteScrapeConfig?.crawlMode || WebsiteScrapeConfigCrawlMode.WEBSITE_SCRAPE_CONFIG_CRAWL_MODE_SINGLE,
      ),
      automaticRefresh: new FormControl(!!this.websiteScrapeConfig?.options?.automaticRefreshTime),
    });
  }

  addExcludedPath(event: MatChipInputEvent): void {
    const value = event.value?.trim();
    if (!value) return;
    if (!value.startsWith('/')) {
      this.excludedPathError.set('WEBSITE_SCRAPE.EXCLUDED_PATHS.INVALID');
      return;
    }
    this.excludedPathError.set('');
    this.excludedPaths.push(value);
    event.chipInput?.clear();
  }

  removeExcludedPath(prefix: string): void {
    this.excludedPaths = this.excludedPaths.filter((p) => p !== prefix);
    this.excludedPathError.set('');
  }

  crawlModeChange(event: MatSelectChange): void {
    if (event.value === WebsiteScrapeConfigCrawlMode.WEBSITE_SCRAPE_CONFIG_CRAWL_MODE_SINGLE) {
      this.excludedPaths = [];
    }
    this.excludedPathError.set('');
  }

  checkProgress(): void {
    if (this.knowledgeSource) {
      this.websiteScrapeConfig = this.knowledgeSource?.config?.websiteScrapeConfig;
      if (!this.knowledgeSource.isSuggested) {
        if (
          this.websiteScrapeConfig?.scrapeProgress !== WebsiteScrapeConfigProgress.WEBSITE_SCRAPE_CONFIG_PROGRESS_DONE
        ) {
          this.pollUntilScrapingFinishes();
        }
        if (!isTrainingFinished(this.knowledgeSource.training)) {
          this.pollUntilTrainingFinishes();
        }
      }
    }
  }

  shouldShowError(): boolean {
    return !!this.knowledgeSource?.config?.websiteScrapeConfig?.error && !this.enableEditingWithError;
  }

  pollUntilScrapingFinishes(): void {
    this.enableEditingWithError = false;
    this.disableSubmit = true;
    defer(() =>
      from(this.knowledgeService.getKnowledgeSource(this.knowledgeSource?.id, this.knowledgeSource?.namespace)),
    )
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        map((ks) => {
          this.knowledgeSource = ks;
          this.websiteScrapeConfig = ks?.config?.websiteScrapeConfig;
          // Update progress
          this.setTotalPagesFound(this.websiteScrapeConfig?.inProgressScrapedPages);
          if (
            this.websiteScrapeConfig?.scrapeProgress !== WebsiteScrapeConfigProgress.WEBSITE_SCRAPE_CONFIG_PROGRESS_DONE
          ) {
            this.disableSubmit = true;
            this.delayProgressScreenClosure = true;
            // throw error to poll again
            throw false;
          }
          if (!this.shouldShowError()) {
            if (this.selectedStep == 0) {
              setTimeout(() => {
                this.stepper.next();
                // This line fixes the console error message: "ExpressionChangedAfterItHasBeenCheckedError: Expression has changed after it was checked."
                this.cdr.detectChanges();
              }, 1000);
            }
          }
          setTimeout(() => {
            this.delayProgressScreenClosure = false;
            this.disableSubmit = false;
          }, 1000);
        }),
        retry({ delay: 5000 }),
        take(1),
      )
      .subscribe();
  }

  private setTotalPagesFound(inProgressScrapedPages: KnowledgeSourceConfigWebsiteScrapeConfigScrapedPage[]) {
    if (inProgressScrapedPages && inProgressScrapedPages.length > 0) {
      let count = 0;
      for (const page of inProgressScrapedPages) {
        if (page.doneScraping) count++;
      }
      this.scrapeProgressPagesFound = count;
      return;
    }

    if (this.websiteScrapeConfig?.scrapeProgress !== WebsiteScrapeConfigProgress.WEBSITE_SCRAPE_CONFIG_PROGRESS_DONE) {
      this.scrapeProgressPagesFound = 0;
    } else {
      this.scrapeProgressPagesFound = this.websiteScrapeConfig.scrapedPages.length;
    }
  }

  pollUntilTrainingFinishes(): void {
    this.disableSubmit = true;
    if (this.onLastStep()) {
      this.shouldShowDone.set(true);
    }
    defer(() =>
      from(this.knowledgeService.getKnowledgeSource(this.knowledgeSource?.id, this.knowledgeSource?.namespace)),
    )
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        map((ks) => {
          this.knowledgeSource = ks;
          if (!isTrainingFinished(this.knowledgeSource.training)) {
            this.disableSubmit = true;
            this.scrapedPagesComponent.delayProgressScreenClosure = true;
            // throw error to poll again
            throw false;
          }
          if (!this.shouldShowError()) {
            if (this.selectedStep == 1) {
              setTimeout(() => {
                if (!this.onLastStep()) {
                  this.stepper.next();
                  // This line fixes the console error message: "ExpressionChangedAfterItHasBeenCheckedError: Expression has changed after it was checked."
                  this.cdr.detectChanges();
                }
              }, 1000);
            }
          }
          setTimeout(() => {
            this.scrapedPagesComponent.delayProgressScreenClosure = false;
            this.disableSubmit = false;
          }, 1000);
        }),
        retry({ delay: 5000 }),
        take(1),
      )
      .subscribe();
  }

  stepChanged(event: StepperSelectionEvent) {
    this.selectedStep = event?.selectedIndex;
    this.stepChange.emit(this.steps[this.selectedStep]);

    if (event.selectedIndex > event.previouslySelectedIndex) {
      event.previouslySelectedStep.completed = true;
    }
    switch (event.selectedIndex) {
      case 1:
        event.selectedStep.completed = true;
        if (!isTrainingFinished(this.knowledgeSource?.training)) {
          this.pollUntilTrainingFinishes();
        }
        break;
      case 2:
        event.selectedStep.completed = true;
        break;
    }
  }

  getCrawlModeOptions(): WebsiteScrapeConfigCrawlMode[] {
    return [
      WebsiteScrapeConfigCrawlMode.WEBSITE_SCRAPE_CONFIG_CRAWL_MODE_SINGLE,
      WebsiteScrapeConfigCrawlMode.WEBSITE_SCRAPE_CONFIG_CRAWL_MODE_RECURSIVE,
      WebsiteScrapeConfigCrawlMode.WEBSITE_SCRAPE_CONFIG_CRAWL_MODE_SITEMAP,
    ];
  }

  getCrawlModeTranslationKey(crawlMode: WebsiteScrapeConfigCrawlMode): string {
    switch (crawlMode) {
      case WebsiteScrapeConfigCrawlMode.WEBSITE_SCRAPE_CONFIG_CRAWL_MODE_SINGLE: {
        return 'EDIT_KNOWLEDGE.WEBSITE_SCRAPE_CONFIG.SINGLE_PAGE';
      }
      case WebsiteScrapeConfigCrawlMode.WEBSITE_SCRAPE_CONFIG_CRAWL_MODE_RECURSIVE: {
        return 'EDIT_KNOWLEDGE.WEBSITE_SCRAPE_CONFIG.FOLLOW_LINKS';
      }
      case WebsiteScrapeConfigCrawlMode.WEBSITE_SCRAPE_CONFIG_CRAWL_MODE_SITEMAP: {
        return 'EDIT_KNOWLEDGE.WEBSITE_SCRAPE_CONFIG.SITEMAP';
      }
      default: {
        return '';
      }
    }
  }

  getScrapeProgressTitle(): string {
    if (
      this.websiteScrapeConfig?.scrapeProgress === WebsiteScrapeConfigProgress.WEBSITE_SCRAPE_CONFIG_PROGRESS_QUEUED
    ) {
      return 'EDIT_KNOWLEDGE.WEBSITE_SCRAPE_CONFIG.SCRAPE_QUEUED';
    } else {
      if (this.scrapeProgressPagesFound > 0) {
        return 'EDIT_KNOWLEDGE.WEBSITE_SCRAPE_CONFIG.SCRAPE_IN_PROGRESS';
      } else {
        return 'EDIT_KNOWLEDGE.WEBSITE_SCRAPE_CONFIG.SCRAPE_QUEUED';
      }
    }
  }

  getPageLimit(): number {
    switch (this.environmentService.getEnvironment()) {
      case Environment.LOCAL:
      case Environment.DEMO:
        return 10;
      case Environment.PROD:
        return 100;
      default:
        return 0;
    }
  }

  async submit(): Promise<void> {
    if (!this.hasUnsavedChanges()) {
      if (!this.onLastStep()) this.stepper.next();
      else this.close();

      return;
    }

    if (!this.isFormValid()) {
      this.snackbarService.openErrorSnack('COMMON.INVALID_FORM');
      return;
    }

    switch (this.selectedStep) {
      case 0: {
        const name =
          this.form.get('name')?.value ||
          this.translateService.instant('EDIT_KNOWLEDGE.WEBSITE_SCRAPE_CONFIG.MY_WEBSITE');
        const oneMonth = `${30 * 24 * 60 * 60}s`;
        const refreshTime = this.form.get('automaticRefresh')?.value ? oneMonth : undefined;
        const url = this.form.get('url')?.value;
        const config = new KnowledgeSourceConfig({
          websiteScrapeConfig: new KnowledgeSourceConfigWebsiteScrapeConfig({
            url: url,
            crawlMode: this.form.get('crawlMode')?.value,
            scrapedPages: this.websiteScrapeConfig?.scrapedPages,
            options: {
              disableAutoTraining: this.websiteScrapeConfig?.options?.disableAutoTraining,
              automaticRefreshTime: refreshTime,
              excludedUrlPaths: this.excludedPaths,
            },
          }),
        });
        const id = await this.knowledgeService.createOrUpdateKnowledgeSource(this.knowledgeSource, name, config);
        this.knowledgeCreated.emit(id);
        this.disableSubmit = true;
        this.enableEditingWithError = false;
        this.form.markAsPristine();
        this.scrapeProgressPagesFound = 0;
        await this.refreshKnowledgeSource(id);
        this.configureStepper();
        this.pollUntilScrapingFinishes();
        break;
      }
      case 1: {
        const id = await this.scrapedPagesComponent?.submit();
        await this.refreshKnowledgeSource(id);
        this.configureStepper();
        this.pollUntilTrainingFinishes();
        break;
      }
      case 2: {
        await this.applicationSelector.setAppsForKnowledgeSource();
        // Close directly without warning check since we just saved
        this.stepChange.emit('');
        this.dialogRef.close();
        break;
      }
    }
  }

  private async refreshKnowledgeSource(id: string) {
    const ks = await this.knowledgeService.getKnowledgeSource(id, this.knowledgeService.buildNamespaceInterface());
    this.knowledgeSource = ks;
    this.websiteScrapeConfig = ks?.config?.websiteScrapeConfig;
  }

  private isFormValid(): boolean {
    switch (this.selectedStep) {
      case 0: {
        const controls = [this.form.get('name'), this.form.get('url')];
        let valid = true;
        controls.forEach((c) => {
          c?.markAsDirty();
          if (!c?.valid) {
            valid = false;
          }
        });
        return valid;
      }
      case 1: {
        return true;
      }
      case 2: {
        return true;
      }
    }
    return false;
  }

  hasUnsavedChanges(): boolean {
    switch (this.selectedStep) {
      case 0: {
        return (
          this.form?.dirty ||
          !stringArraysEqualUnordered(
            this.excludedPaths || [],
            this.knowledgeSource?.config?.websiteScrapeConfig?.options?.excludedUrlPaths || [],
          )
        );
      }
      case 1: {
        return this.scrapedPagesComponent?.hasUnsavedChanges() ?? false;
      }
      case 2: {
        return this.applicationSelector?.hasUnsavedChanges ?? false;
      }
    }
    return false;
  }

  async rescrape(): Promise<void> {
    if (!this.knowledgeSource) {
      return;
    }
    await this.knowledgeService.rescrapeWebsite(this.knowledgeSource);
    this.knowledgeSource = await this.knowledgeService.getKnowledgeSource(
      this.knowledgeSource.id,
      this.knowledgeService.buildNamespaceInterface(),
    );
    this.goToStep(0);
    this.checkProgress();
  }

  editError(): void {
    this.disableSubmit = false;
    this.enableEditingWithError = true;
  }

  ngAfterViewInit() {
    if (this.stepper) {
      if (this.stepOption) {
        const stepIndex = this.steps.indexOf(this.stepOption);
        if (stepIndex > 0) {
          const firstStep = this.stepper.steps.get(0);
          if (firstStep) {
            firstStep.completed = false;
          }
          this.goToStep(stepIndex);
        }
      }
      this.stepper.steps.forEach((step, idx) => {
        step.select = () => {
          // do not allow a latter step to be jumped to if stepper is linear
          if (this.stepper.linear && !step.completed) {
            return;
          }

          if (!this.hasUnsavedChanges()) {
            this.stepper.selectedIndex = idx;
            return;
          }

          this.confirmationModal
            .openModal({
              type: 'warn',
              title: this.translateService.instant('UNSAVED_CHANGES.TITLE'),
              message: this.translateService.instant('UNSAVED_CHANGES.MESSAGE'),
              confirmButtonText: this.translateService.instant('COMMON.YES'),
              cancelButtonText: this.translateService.instant('COMMON.NO'),
            })
            .subscribe((confirmed) => {
              if (confirmed) {
                this.stepper.selectedIndex = idx;
              }
            });
        };
      });
      this.configureStepper();
    }
  }

  async createWebChatWidget(): Promise<void> {
    await this.webChatWidgetService.createWebChatWidget({
      dialogRef: this.dialogRef,
      knowledgeSource: this.knowledgeSource,
      applicationSelector: this.applicationSelector,
    });
  }

  getTitle(): string {
    if (this.knowledgeSource?.isSuggested) {
      return this.knowledgeSource?.name;
    }
    if (!this.data?.knowledgeSourceKey) {
      return 'ADD_KNOWLEDGE';
    }
    return 'EDIT_KNOWLEDGE.TITLE';
  }

  showBack(): boolean {
    return this.selectedStep > 0;
  }

  private configureStepper() {
    if (this.knowledgeSource?.isSuggested) return;

    this.stepper.linear = true;
    if (this.knowledgeSource && !this.knowledgeSource.isSuggested) {
      if (
        this.knowledgeSource.config.websiteScrapeConfig.scrapeProgress !==
        WebsiteScrapeConfigProgress.WEBSITE_SCRAPE_CONFIG_PROGRESS_DONE ||
        this.knowledgeSource.config.websiteScrapeConfig.error
      ) {
        this.stepper.selectedIndex = 0;
        const secondStep = this.stepper.steps.get(1);
        if (secondStep) {
          secondStep.completed = false;
        }
        const thirdStep = this.stepper.steps.get(2);
        if (thirdStep) {
          thirdStep.completed = false;
        }
      } else if (!isTrainingFinished(this.knowledgeSource.training)) {
        this.stepper.selectedIndex = 1;
        const thirdStep = this.stepper.steps.get(2);
        if (thirdStep) {
          thirdStep.completed = false;
        }
      } else {
        this.stepper.linear = false;
        const secondStep = this.stepper.steps.get(1);
        if (secondStep) {
          secondStep.completed = true;
        }
        const thirdStep = this.stepper.steps.get(2);
        if (thirdStep) {
          thirdStep.completed = true;
        }
      }
    }
  }

  goToStep(stepIndex: number): void {
    this.selectedStep = stepIndex;
    this.stepper.selectedIndex = this.selectedStep;
  }

  back(): void {
    this.warnUnsavedChanged(() => {
      this.stepper.previous();
    });
  }

  onLastStep(): boolean {
    return this.selectedStep === this.steps.length - 1;
  }

  warnUnsavedChanged(func: () => void): void {
    if (!this.hasUnsavedChanges()) {
      func();
      return;
    }

    this.confirmationModal
      .openModal({
        type: 'warn',
        title: this.translateService.instant('UNSAVED_CHANGES.TITLE'),
        message: this.translateService.instant('UNSAVED_CHANGES.MESSAGE'),
        confirmButtonText: this.translateService.instant('COMMON.YES'),
        cancelButtonText: this.translateService.instant('COMMON.NO'),
      })
      .subscribe((confirmed) => {
        if (confirmed) {
          func();
        }
      });
  }

  close(): void {
    this.warnUnsavedChanged(() => {
      this.stepChange.emit('');
      this.dialogRef.close();
    });
  }

  getSubmitAction(): string {
    if (this.onLastStep()) {
      return 'COMMON.SAVE';
    }
    return 'COMMON.NEXT';
  }

  async rescrapeInitiated(): Promise<void> {
    // rescrape can only be initiated for website scrape component, go to the scraped pages step
    this.knowledgeSource = await this.knowledgeService.getKnowledgeSource(
      this.knowledgeSource?.id,
      this.knowledgeService.buildNamespaceInterface(),
    );
    this.goToStep(0);
    this.checkProgress();
  }
}

function stringArraysEqualUnordered(arr1: string[], arr2: string[]): boolean {
  if (arr1.length !== arr2.length) return false;
  const set1 = new Set(arr1);
  const set2 = new Set(arr2);
  if (set1.size !== set2.size) return false; // Different number of unique elements
  return [...set1].every((item) => set2.has(item));
}
